import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  X, 
  RefreshCw,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Users,
  ChevronDown,
  Star,
  TrendingUp,
  Building,
  Sparkles,
  ArrowUpDown
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PartnerCard from '../components/ConsPartnerCard';

const CLOUDFLARE_PROFILES_URL = 'https://pub-2a10bd66114a4441b95e9249adee01cf.r2.dev';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: [0.25, 0.46, 0.45, 0.94]
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const cardAnimation = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { 
      type: "spring", 
      damping: 15,
      duration: 0.6
    }
  }
};

// Utility functions
const getTipoParam = (path) => {
  const segments = path.split('/');
  const lastSegment = segments[segments.length - 1];

  switch(lastSegment) {
    case 'vendedores':
      return 'vendedor';
    case 'constructoras':
      return 'constructora';
    case 'inmobiliarias':
      return 'inmobiliaria';
    case 'all':
    case 'usuarios':
      return 'vendedor';
    default:
      return 'vendedor';
  }
};

const getTipoId = (tipoParam) => {
  switch(tipoParam) {
    case 'vendedor':
      return 2;
    case 'constructora':
      return 3;
    case 'inmobiliaria':
      return 4;
    default:
      return 2;
  }
};

const getTitleByPath = (path) => {
  const segments = path.split('/');
  const lastSegment = segments[segments.length - 1];

  switch(lastSegment) {
    case 'vendedores':
      return { title: 'Vendedores', icon: Users, color: 'indigo' };
    case 'constructoras':
      return { title: 'Constructoras', icon: Building, color: 'purple' };
    case 'inmobiliarias':
      return { title: 'Inmobiliarias', icon: TrendingUp, color: 'blue' };
    case 'all':
    case 'usuarios':
      return { title: 'Todos los Usuarios', icon: Users, color: 'green' };
    default:
      return { title: 'Usuarios', icon: Users, color: 'indigo' };
  }
};

// Enhanced Components
const SearchInput = ({ value, onChange, onClear }) => (
  <motion.div 
    className="relative max-w-lg w-full"
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.5 }}
  >
    <div className="relative group">
      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5 transition-all duration-300 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 group-focus-within:scale-105" />
      <input
        type="text"
        placeholder="Buscar por nombre..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-12 pr-12 py-4 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border-2 border-white/20 dark:border-gray-600/50 rounded-xl 
                 focus:ring-3 focus:ring-indigo-500/30 focus:border-indigo-400/50 dark:focus:border-indigo-500/50
                 shadow-lg dark:shadow-black/50 transition-all duration-300 text-gray-900 dark:text-gray-100
                 placeholder:text-gray-500 dark:placeholder:text-gray-400"
      />
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-indigo-500/0 via-indigo-500/5 to-indigo-500/0 
                      opacity-0 focus-within:opacity-100 transition-opacity duration-300 pointer-events-none" />
      {value && (
        <motion.button 
          onClick={onClear}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 
                   text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <X className="w-4 h-4" />
        </motion.button>
      )}
    </div>
  </motion.div>
);

const FilterDropdown = ({ isOpen, onToggle, onSort, sortBy }) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onToggle(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onToggle]);

  const sortOptions = [
    { id: 'rating_desc', label: 'Mejor calificados', icon: Star },
    { id: 'rating_asc', label: 'Menor calificación', icon: Star },
    { id: 'name_asc', label: 'Nombre: A-Z', icon: ArrowUpDown },
    { id: 'name_desc', label: 'Nombre: Z-A', icon: ArrowUpDown }
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        onClick={() => onToggle(!isOpen)}
        className={`flex items-center gap-2 px-6 py-4 rounded-xl font-medium transition-all duration-300 shadow-lg dark:shadow-black/50 ${
          isOpen 
            ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white' 
            : 'bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm text-gray-700 dark:text-gray-200 border-2 border-white/20 dark:border-gray-600/50 hover:bg-white/80 dark:hover:bg-gray-800/80'
        }`}
        whileHover={{ scale: 1.02, y: -1 }}
        whileTap={{ scale: 0.98 }}
      >
        <Filter className="w-5 h-5" />
        <span>Filtros</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${
          isOpen ? 'rotate-180' : ''
        }`} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute top-full left-0 mt-2 w-64 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-xl shadow-xl dark:shadow-black/50 border border-white/20 dark:border-gray-600 z-50 overflow-hidden"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="p-2">
              {sortOptions.map((option, index) => (
                <motion.button
                  key={option.id}
                  onClick={() => {
                    onSort(option.id);
                    onToggle(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                    sortBy === option.id 
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md' 
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <option.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium">{option.label}</span>
                  {sortBy === option.id && <Sparkles className="w-4 h-4 ml-auto" />}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PaginationButton = ({ onClick, disabled, children, variant = "default" }) => (
  <motion.button
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center justify-center px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
      variant === "current" 
        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg dark:shadow-xl' 
        : disabled
          ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
          : 'bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm text-gray-700 dark:text-gray-200 border-2 border-white/20 dark:border-gray-600/50 hover:bg-white/80 dark:hover:bg-gray-800/80 shadow-lg dark:shadow-black/50'
    }`}
    whileHover={!disabled ? { scale: 1.05, y: -2 } : {}}
    whileTap={!disabled ? { scale: 0.95 } : {}}
  >
    {children}
  </motion.button>
);

const LoadingCard = ({ index }) => (
  <motion.div
    className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-2xl shadow-lg dark:shadow-black/50 border border-white/20 dark:border-gray-600/50 overflow-hidden"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 }}
  >
    <div className="animate-pulse">
      <div className="h-48 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600" />
      <div className="p-6 space-y-4">
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded-lg w-3/4" />
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded-lg w-1/2" />
        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded-lg w-2/3" />
      </div>
    </div>
  </motion.div>
);

const UserTypePage = () => {
  const location = useLocation();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const ITEMS_PER_PAGE = 12;
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState('');

  const tipoParam = getTipoParam(location.pathname);
  const tipoId = getTipoId(tipoParam);
  const pageInfo = getTitleByPath(location.pathname);

  const processUserData = (user) => {
    if (!user.foto_perfil) return { ...user, tipo_usuario_id: tipoId, foto_perfil: null };
  
    const cleanUrl = user.foto_perfil
      .replace('https://backend-production-1786.up.railway.app/profiles/', '')
      .replace('/profiles/', '')
      .replace('profiles/', '')
      .replace(/^\//, '');
  
    return {
      ...user,
      tipo_usuario_id: tipoId,
      foto_perfil: `${CLOUDFLARE_PROFILES_URL}/${cleanUrl}`
    };
  };

  const fetchUsers = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const offset = (page - 1) * ITEMS_PER_PAGE;
      
      const response = await fetch(
        `https://backend-production-1786.up.railway.app/api/vendedores?tipo=${tipoParam}&limit=${ITEMS_PER_PAGE}&offset=${offset}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.resultados) {
        throw new Error('Formato de respuesta inválido');
      }

      const processedUsers = data.resultados.map(processUserData);

      setUsers(processedUsers);
      setTotalPages(Math.ceil(data.total / ITEMS_PER_PAGE));
      
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      setError(
        error.message || 
        'No se pudieron cargar los usuarios. Por favor, intenta de nuevo más tarde.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchUsers(1);
  }, [location.pathname]);

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage]);

  const handleSort = (sortType) => {
    setSortBy(sortType);
    let sortedUsers = [...users];

    switch (sortType) {
      case 'rating_asc':
        sortedUsers.sort((a, b) => a.calificacion - b.calificacion);
        break;
      case 'rating_desc':
        sortedUsers.sort((a, b) => b.calificacion - a.calificacion);
        break;
      case 'name_asc':
        sortedUsers.sort((a, b) => a.nombre.localeCompare(b.nombre));
        break;
      case 'name_desc':
        sortedUsers.sort((a, b) => b.nombre.localeCompare(a.nombre));
        break;
      default:
        break;
    }

    setUsers(sortedUsers);
  };

  const filteredUsers = users.filter(user =>
    user.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const colorSchemes = {
    indigo: { gradient: "from-indigo-500 to-purple-600", glow: "bg-indigo-500/20" },
    purple: { gradient: "from-purple-500 to-pink-600", glow: "bg-purple-500/20" },
    blue: { gradient: "from-blue-500 to-cyan-600", glow: "bg-blue-500/20" },
    green: { gradient: "from-green-500 to-emerald-600", glow: "bg-green-500/20" }
  };

  const colorScheme = colorSchemes[pageInfo.color] || colorSchemes.indigo;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-blue-800 to-indigo-900 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-400/20 blur-2xl"
                style={{
                  width: `${Math.random() * 300 + 100}px`,
                  height: `${Math.random() * 300 + 100}px`,
                  top: `${Math.random() * 90}%`,
                  left: `${Math.random() * 90}%`,
                }}
                animate={{
                  x: [0, Math.random() * 50 - 25, 0],
                  y: [0, Math.random() * 50 - 25, 0],
                  scale: [1, Math.random() * 0.3 + 1, 1],
                }}
                transition={{
                  duration: Math.random() * 10 + 15,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>
          
          {/* Mesh Grid Overlay */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}
          />
        </div>

        <div className="relative py-24">
          <motion.div
            className="max-w-7xl mx-auto px-4 sm:px-6 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div 
              className="inline-flex items-center justify-center mb-6"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className={`relative p-4 rounded-2xl bg-gradient-to-br ${colorScheme.gradient} shadow-xl`}>
                <div className={`absolute inset-0 ${colorScheme.glow} rounded-2xl blur-lg opacity-50`} />
                <pageInfo.icon className="w-12 h-12 text-white relative z-10" />
              </div>
            </motion.div>
            
            <motion.h1 
              className="text-5xl md:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-200">
                {pageInfo.title}
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-xl text-white/80 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Explora y conecta con nuestros profesionales registrados. 
              Encuentra el socio perfecto para tu próximo proyecto inmobiliario.
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 -mt-16 relative z-10">
        {/* Search and Filter Bar */}
        <motion.div 
          className="backdrop-blur-xl bg-white/10 dark:bg-white/5 rounded-2xl shadow-xl dark:shadow-black/50 p-6 mb-12 border border-white/20 dark:border-white/10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <SearchInput 
              value={searchTerm}
              onChange={setSearchTerm}
              onClear={() => setSearchTerm('')}
            />
            
            <FilterDropdown 
              isOpen={isFilterOpen}
              onToggle={setIsFilterOpen}
              onSort={handleSort}
              sortBy={sortBy}
            />
          </div>
        </motion.div>

        {/* Results Count */}
        {!loading && !error && (
          <motion.div
            className="mb-8 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm ? (
                <>Mostrando {filteredUsers.length} resultados para "<span className="font-semibold text-indigo-600 dark:text-indigo-400">{searchTerm}</span>"</>
              ) : (
                <>Mostrando {filteredUsers.length} {pageInfo.title.toLowerCase()}</>
              )}
            </p>
          </motion.div>
        )}

        {/* Content */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {[...Array(9)].map((_, i) => (
                <LoadingCard key={i} index={i} />
              ))}
            </motion.div>
          ) : error ? (
            <motion.div
              key="error"
              className="flex flex-col items-center justify-center py-20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <div className="backdrop-blur-xl bg-white/10 dark:bg-white/5 rounded-3xl shadow-xl dark:shadow-black/50 p-12 max-w-lg mx-auto text-center border border-white/20 dark:border-white/10">
                <motion.div 
                  className="w-20 h-20 mx-auto mb-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center"
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <AlertCircle className="w-10 h-10 text-red-500 dark:text-red-400" />
                </motion.div>
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">
                  Error al cargar usuarios
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-8">{error}</p>
                <motion.button
                  onClick={() => fetchUsers(currentPage)}
                  className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl 
                           font-medium shadow-lg hover:shadow-xl dark:shadow-xl dark:hover:shadow-2xl
                           transition-all duration-300 flex items-center gap-3 mx-auto"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <RefreshCw className="w-5 h-5" />
                  Reintentar
                </motion.button>
              </div>
            </motion.div>
          ) : filteredUsers.length === 0 ? (
            <motion.div
              key="empty"
              className="flex flex-col items-center justify-center py-20"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <div className="text-center">
                <motion.div 
                  className="w-20 h-20 mx-auto mb-6 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center"
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Search className="w-10 h-10 text-gray-400 dark:text-gray-500" />
                </motion.div>
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">
                  No se encontraron resultados
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-8">
                  {searchTerm 
                    ? `No hay usuarios que coincidan con "${searchTerm}"`
                    : 'No hay usuarios disponibles en este momento'
                  }
                </p>
                {searchTerm && (
                  <motion.button
                    onClick={() => setSearchTerm('')}
                    className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl 
                             font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Limpiar búsqueda
                  </motion.button>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {filteredUsers.map((user, index) => (
                <motion.div
                  key={user.id}
                  variants={cardAnimation}
                  whileHover={{ y: -8, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <PartnerCard partner={user} />
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <motion.div 
            className="flex justify-center items-center gap-4 mt-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <PaginationButton
              onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-5 h-5" />
            </PaginationButton>
            
            <div className="flex items-center gap-2">
              {/* Show page numbers */}
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }
                
                return (
                  <PaginationButton
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    variant={currentPage === pageNumber ? "current" : "default"}
                  >
                    {pageNumber}
                  </PaginationButton>
                );
              })}
            </div>
            
            <PaginationButton
              onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="w-5 h-5" />
            </PaginationButton>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default UserTypePage;